import { createContext, use, useEffect, useMemo, useRef, type ReactNode } from 'react';

/** A `vm.open_layout` command pushed by the server to an operator (ADR-039). */
export interface VmOpenLayoutCommand {
  workstationGuid: string;
  monitorGuid: string;
  layoutId: string | null;
  layoutName: string;
}

type Handler = (cmd: VmOpenLayoutCommand) => void;

interface VmBus {
  subscribe: (h: Handler) => () => void;
  dispatch: Handler;
}

const noopBus: VmBus = { subscribe: () => () => undefined, dispatch: () => undefined };

const VmContext = createContext<VmBus>(noopBus);

/** Fan-out of virtual-matrix commands; independent of the events.view gate. */
export function VirtualMatrixProvider({ children }: { children: ReactNode }) {
  const handlers = useRef(new Set<Handler>());
  const bus = useMemo<VmBus>(
    () => ({
      subscribe: (h) => {
        handlers.current.add(h);
        return () => {
          handlers.current.delete(h);
        };
      },
      dispatch: (cmd) => {
        handlers.current.forEach((h) => {
          h(cmd);
        });
      },
    }),
    [],
  );
  return <VmContext value={bus}>{children}</VmContext>;
}

/** Used by the live socket to push a command to subscribers. */
// eslint-disable-next-line react-refresh/only-export-components
export function useVmDispatch(): (cmd: VmOpenLayoutCommand) => void {
  return use(VmContext).dispatch;
}

/** Calls `handler` for every `vm.open_layout` command that arrives while mounted. */
// eslint-disable-next-line react-refresh/only-export-components
export function useVmOpenLayout(handler: (cmd: VmOpenLayoutCommand) => void): void {
  const bus = use(VmContext);
  const ref = useRef(handler);
  useEffect(() => {
    ref.current = handler;
  });
  useEffect(
    () =>
      bus.subscribe((cmd) => {
        ref.current(cmd);
      }),
    [bus],
  );
}
